import React, { useState, useRef, useEffect, useCallback } from "react";
import { jsPDF } from "jspdf";

// ---------------------------------------------------------------------------
// PAGE OPTIONS
// ---------------------------------------------------------------------------

const pageOptions = {
  "305x460_portrait": {
    width: 305,
    height: 460,
    label: "305 × 460 Portrait",
  },

  "305x460_landscape": {
    width: 460,
    height: 305,
    label: "305 × 460 Landscape",
  },

  "457x635_portrait": {
    width: 457,
    height: 635,
    label: "457 × 635 Portrait — Offset (635 × 457 mm H × W)",
  },

  "457x635_landscape": {
    width: 635,
    height: 457,
    label: "635 × 457 Landscape — Offset",
  },

  a4_portrait: {
    width: 210,
    height: 297,
    label: "A4 Portrait",
  },

  a4_landscape: {
    width: 297,
    height: 210,
    label: "A4 Landscape",
  },
};

// ---------------------------------------------------------------------------
// CARD DEFAULT SIZE
// ---------------------------------------------------------------------------

const CARD_W_MM = 3.57 * 25.4; // ~90.7 mm
const CARD_H_MM = 2.05 * 25.4; // ~52.1 mm

// ---------------------------------------------------------------------------
// GRID OPTIONS
//
// IMPORTANT:
// Page margin and grid gap are now completely independent.
//
// outerMargin = space from page edge to first/last card
// gridGap     = space between neighboring cards
// ---------------------------------------------------------------------------

const gridOptions = [
  {
    id: "24_portrait",
    label: "24 Cards — Portrait",
    computeGrid: (pageW, pageH, outerMargin, gridGap) => {
      const availableW = Math.max(0, pageW - outerMargin * 2);
      const availableH = Math.max(0, pageH - outerMargin * 2);

      const cols = Math.max(
        1,
        Math.floor((availableW + gridGap) / (CARD_W_MM + gridGap))
      );

      const rows = Math.max(
        1,
        Math.floor((availableH + gridGap) / (CARD_H_MM + gridGap))
      );

      // When "No Space" is selected, use every available millimetre.
      // This removes the unused remainder that standard 90.7 × 52.1 mm
      // cards would otherwise leave on a 305 × 460 mm sheet.
      if (gridGap === 0) {
        return {
          cols,
          rows,
          imgW: availableW / cols,
          imgH: availableH / rows,
        };
      }

      return {
        cols,
        rows,
        imgW: CARD_W_MM,
        imgH: CARD_H_MM,
      };
    },
  },

  {
    id: "24_landscape",
    label: "24 Cards — Landscape (5 × 5 fit)",
    computeGrid: (pageW, pageH, outerMargin, gridGap) => {
      const cols = 5;
      const rows = 5;

      const availableW = Math.max(
        0,
        pageW - outerMargin * 2 - (cols - 1) * gridGap
      );

      const availableH = Math.max(
        0,
        pageH - outerMargin * 2 - (rows - 1) * gridGap
      );

      return {
        cols,
        rows,
        imgW: availableW / cols,
        imgH: availableH / rows,
      };
    },
  },

  {
    id: "12",
    label: "12 Cards (4 × 3)",
    computeGrid: (pageW, pageH, outerMargin, gridGap) => {
      const cols = 3;
      const rows = 4;

      const availableW = Math.max(
        0,
        pageW - outerMargin * 2 - (cols - 1) * gridGap
      );

      const availableH = Math.max(
        0,
        pageH - outerMargin * 2 - (rows - 1) * gridGap
      );

      return {
        cols,
        rows,
        imgW: availableW / cols,
        imgH: availableH / rows,
      };
    },
  },

  {
    id: "9",
    label: "9 Cards (3 × 3)",
    computeGrid: (pageW, pageH, outerMargin, gridGap) => {
      const cols = 3;
      const rows = 3;

      const availableW = Math.max(
        0,
        pageW - outerMargin * 2 - (cols - 1) * gridGap
      );

      const availableH = Math.max(
        0,
        pageH - outerMargin * 2 - (rows - 1) * gridGap
      );

      return {
        cols,
        rows,
        imgW: availableW / cols,
        imgH: availableH / rows,
      };
    },
  },

  {
    id: "6",
    label: "6 Cards (3 × 2)",
    computeGrid: (pageW, pageH, outerMargin, gridGap) => {
      const cols = 2;
      const rows = 3;

      const availableW = Math.max(
        0,
        pageW - outerMargin * 2 - (cols - 1) * gridGap
      );

      const availableH = Math.max(
        0,
        pageH - outerMargin * 2 - (rows - 1) * gridGap
      );

      return {
        cols,
        rows,
        imgW: availableW / cols,
        imgH: availableH / rows,
      };
    },
  },

  {
    id: "4",
    label: "4 Cards (2 × 2)",
    computeGrid: (pageW, pageH, outerMargin, gridGap) => {
      const cols = 2;
      const rows = 2;

      const availableW = Math.max(
        0,
        pageW - outerMargin * 2 - (cols - 1) * gridGap
      );

      const availableH = Math.max(
        0,
        pageH - outerMargin * 2 - (rows - 1) * gridGap
      );

      return {
        cols,
        rows,
        imgW: availableW / cols,
        imgH: availableH / rows,
      };
    },
  },

  {
    id: "1x2",
    label: "2 Cards (1 × 2) — Large",
    computeGrid: (pageW, pageH, outerMargin, gridGap) => {
      const cols = 2;
      const rows = 1;

      const availableW = Math.max(
        0,
        pageW - outerMargin * 2 - (cols - 1) * gridGap
      );

      const availableH = Math.max(
        0,
        pageH - outerMargin * 2
      );

      return {
        cols,
        rows,
        imgW: availableW / cols,
        imgH: availableH,
      };
    },
  },

  {
    id: "1x1",
    label: "1 Card (1 × 1) — Full Page",
    computeGrid: (pageW, pageH, outerMargin, gridGap) => {
      const cols = 1;
      const rows = 1;

      const availableW = Math.max(
        0,
        pageW - outerMargin * 2
      );

      const availableH = Math.max(
        0,
        pageH - outerMargin * 2
      );

      return {
        cols,
        rows,
        imgW: availableW,
        imgH: availableH,
      };
    },
  },
];

const BIG_GRID_IDS = new Set(["1x1", "1x2"]);

const MODES = ["single", "bulk", "zip"];

const MODE_LABELS = {
  single: "Single Image",
  bulk: "Bulk Upload",
  zip: "ZIP Upload",
};

// ---------------------------------------------------------------------------
// IMAGE HELPERS
// ---------------------------------------------------------------------------

const loadImageObj = (src) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();

    img.onload = () => resolve(img);

    img.onerror = () =>
      reject(new Error("Unable to load image"));

    img.src = src;

    if (img.complete && img.naturalWidth > 0) {
      resolve(img);
    }
  });

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => resolve(e.target.result);

    reader.onerror = () =>
      reject(new Error("Read failed"));

    reader.readAsDataURL(file);
  });

// ---------------------------------------------------------------------------
// CONTAIN FIT
// ---------------------------------------------------------------------------

function containFit(natW, natH, maxW, maxH) {
  if (!natW || !natH || maxW <= 0 || maxH <= 0) {
    return {
      w: Math.max(0, maxW),
      h: Math.max(0, maxH),
    };
  }

  const scale = Math.min(
    maxW / natW,
    maxH / natH
  );

  return {
    w: natW * scale,
    h: natH * scale,
  };
}

function adjustImageSize(
  baseW,
  baseH,
  widthAdjust,
  heightAdjust
) {
  return {
    w: Math.max(0.1, baseW + widthAdjust),
    h: Math.max(0.1, baseH + heightAdjust),
  };
}

// ---------------------------------------------------------------------------
// IMAGE SIZE FOR GRID CELL
// ---------------------------------------------------------------------------
// fillCell=true means the image occupies the complete cell.
// This is used for "0 mm space between cards" so the image itself cannot
// leave contain-fit white bands inside an otherwise gap-free grid.
// Width/height adjustments are still applied afterwards.
// ---------------------------------------------------------------------------

function getGridImageSize(
  natW,
  natH,
  maxW,
  maxH,
  widthAdjust,
  heightAdjust,
  fillCell = false
) {
  const base = fillCell
    ? { w: maxW, h: maxH }
    : containFit(
        natW,
        natH,
        maxW,
        maxH
      );

  return adjustImageSize(
    base.w,
    base.h,
    widthAdjust,
    heightAdjust
  );
}

function naturalSize(imgObj) {
  return {
    w: imgObj.naturalWidth || imgObj.width,
    h: imgObj.naturalHeight || imgObj.height,
  };
}

// ---------------------------------------------------------------------------
// ROTATE IMAGE
// ---------------------------------------------------------------------------

function makeRotatedCanvas(imgObj) {
  const { w: natW, h: natH } = naturalSize(imgObj);

  const canvas = document.createElement("canvas");

  canvas.width = natH;
  canvas.height = natW;

  const ctx = canvas.getContext("2d");

  ctx.translate(natH, 0);
  ctx.rotate(Math.PI / 2);

  ctx.drawImage(
    imgObj,
    0,
    0,
    natW,
    natH
  );

  return canvas;
}

function getRenderable(imgObj, rotate) {
  if (rotate) {
    const canvas = makeRotatedCanvas(imgObj);

    return {
      drawable: canvas,
      w: canvas.width,
      h: canvas.height,
    };
  }

  const { w, h } = naturalSize(imgObj);

  return {
    drawable: imgObj,
    w,
    h,
  };
}

// ---------------------------------------------------------------------------
// GRID POSITION
//
// This is the central geometry function.
//
// It guarantees:
//
// outerMargin = page-edge margin
// gridGap     = ONLY space between cards
//
// There is NEVER an accidental extra gap.
// ---------------------------------------------------------------------------

function getGridPosition({
  row,
  col,
  cols,
  rows,
  pageW,
  pageH,
  outerMargin,
  gridGap,
  cellW,
  cellH,
}) {
  const usedW =
    cols * cellW +
    Math.max(0, cols - 1) * gridGap;

  const usedH =
    rows * cellH +
    Math.max(0, rows - 1) * gridGap;

  // If margin is specified, the grid is positioned inside that margin.
  //
  // Because the cells are calculated from the available area,
  // this should normally equal outerMargin.
  //
  // The centering also keeps the layout perfectly balanced if
  // a fractional measurement occurs.

  const availableW =
    pageW - outerMargin * 2;

  const availableH =
    pageH - outerMargin * 2;

  const startX =
    outerMargin +
    Math.max(0, (availableW - usedW) / 2);

  const startY =
    outerMargin +
    Math.max(0, (availableH - usedH) / 2);

  return {
    x: startX + col * (cellW + gridGap),
    y: startY + row * (cellH + gridGap),
  };
}

// ---------------------------------------------------------------------------
// SCISSOR CUT MARKS — PDF
// ---------------------------------------------------------------------------

function drawScissorCutMarks(pdf, pageW, pageH) {
  const midY = pageH / 2;

  const lineLen = 8;
  const scissorW = 4;
  const edgeOffset = 2;

  pdf.setDrawColor(150, 150, 150);
  pdf.setLineWidth(0.15);

  const drawScissor = (cx, cy, flip) => {
    const dir = flip ? -1 : 1;

    // Blades
    pdf.setFillColor(150, 150, 150);

    pdf.ellipse(
      cx,
      cy - 1,
      0.9,
      0.55,
      "FD"
    );

    pdf.ellipse(
      cx,
      cy + 1,
      0.9,
      0.55,
      "FD"
    );

    // Handles
    pdf.line(
      cx + dir * 0.8,
      cy - 1,
      cx + dir * (0.8 + lineLen),
      cy - 1.8
    );

    pdf.line(
      cx + dir * 0.8,
      cy + 1,
      cx + dir * (0.8 + lineLen),
      cy + 1.8
    );

    // Pivot
    pdf.setFillColor(255, 255, 255);

    pdf.circle(
      cx,
      cy,
      0.45,
      "FD"
    );

    pdf.setFillColor(150, 150, 150);

    pdf.circle(
      cx,
      cy,
      0.3,
      "F"
    );
  };

  const leftX =
    edgeOffset + scissorW / 2;

  const rightX =
    pageW - edgeOffset - scissorW / 2;

  drawScissor(
    leftX,
    midY,
    false
  );

  drawScissor(
    rightX,
    midY,
    true
  );

  pdf.setDrawColor(0, 0, 0);
  pdf.setFillColor(0, 0, 0);
}

// ---------------------------------------------------------------------------
// SCISSOR CUT MARKS — CANVAS
// ---------------------------------------------------------------------------

function drawScissorCutMarksCanvas(
  ctx,
  pageW,
  pageH,
  PPM
) {
  const midY =
    (pageH / 2) * PPM;

  const lineLen =
    8 * PPM;

  const edgeOffset =
    2 * PPM;

  const scissorW =
    4 * PPM;

  ctx.save();

  ctx.strokeStyle =
    "rgba(150,150,150,0.8)";

  ctx.fillStyle =
    "rgba(150,150,150,0.8)";

  ctx.lineWidth = 0.5;

  const drawScissorCtx = (
    cx,
    cy,
    dir
  ) => {
    // Upper blade
    ctx.beginPath();

    ctx.ellipse(
      cx,
      cy - 1 * PPM,
      0.9 * PPM,
      0.55 * PPM,
      0,
      0,
      Math.PI * 2
    );

    ctx.fill();

    // Lower blade
    ctx.beginPath();

    ctx.ellipse(
      cx,
      cy + 1 * PPM,
      0.9 * PPM,
      0.55 * PPM,
      0,
      0,
      Math.PI * 2
    );

    ctx.fill();

    // Upper handle
    ctx.beginPath();

    ctx.moveTo(
      cx + dir * 0.8 * PPM,
      cy - 1 * PPM
    );

    ctx.lineTo(
      cx +
        dir *
          (0.8 * PPM + lineLen),
      cy - 1.8 * PPM
    );

    ctx.stroke();

    // Lower handle
    ctx.beginPath();

    ctx.moveTo(
      cx + dir * 0.8 * PPM,
      cy + 1 * PPM
    );

    ctx.lineTo(
      cx +
        dir *
          (0.8 * PPM + lineLen),
      cy + 1.8 * PPM
    );

    ctx.stroke();

    // Pivot
    ctx.fillStyle = "white";

    ctx.beginPath();

    ctx.arc(
      cx,
      cy,
      0.45 * PPM,
      0,
      Math.PI * 2
    );

    ctx.fill();

    ctx.fillStyle =
      "rgba(150,150,150,0.9)";

    ctx.beginPath();

    ctx.arc(
      cx,
      cy,
      0.3 * PPM,
      0,
      Math.PI * 2
    );

    ctx.fill();

    ctx.fillStyle =
      "rgba(150,150,150,0.8)";
  };

  drawScissorCtx(
    edgeOffset + scissorW / 2,
    midY,
    1
  );

  drawScissorCtx(
    pageW * PPM -
      edgeOffset -
      scissorW / 2,
    midY,
    -1
  );

  ctx.restore();
}

// ---------------------------------------------------------------------------
// DRAW IMAGE
// ---------------------------------------------------------------------------

function drawContainImageCanvas(
  ctx,
  imgObj,
  x,
  y,
  maxW,
  maxH,
  rotate,
  widthAdjust = 0,
  heightAdjust = 0,
  fillCell = false
) {
  const {
    drawable,
    w: natW,
    h: natH,
  } = getRenderable(
    imgObj,
    rotate
  );

  const {
    w: dw,
    h: dh,
  } = getGridImageSize(
    natW,
    natH,
    maxW,
    maxH,
    widthAdjust,
    heightAdjust,
    fillCell
  );

  const dx =
    x + (maxW - dw) / 2;

  const dy =
    y + (maxH - dh) / 2;

  ctx.drawImage(
    drawable,
    dx,
    dy,
    dw,
    dh
  );
}

function drawPlaceholderCanvas(
  ctx,
  x,
  y,
  w,
  h,
  PPM
) {
  ctx.fillStyle = "#f1f5f9";

  ctx.fillRect(
    x,
    y,
    w,
    h
  );

  ctx.fillStyle = "#94a3b8";

  ctx.font =
    `${Math.round(
      8 * PPM * 0.35
    )}px sans-serif`;

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.fillText(
    "card",
    x + w / 2,
    y + h / 2
  );
}

// ---------------------------------------------------------------------------
// THUMBNAIL
// ---------------------------------------------------------------------------

function Thumbnail({
  src,
  index,
  selected,
  onSelect,
  onRemove,
}) {
  return (
    <div
      onClick={() =>
        onSelect(index)
      }
      style={{
        position: "relative",
        width: 64,
        height: 40,
        border:
          `2px solid ${
            selected
              ? "#38bdf8"
              : "#1e293b"
          }`,
        borderRadius: 5,
        overflow: "hidden",
        cursor: "pointer",
        flexShrink: 0,
        background: "#0f172a",
      }}
    >
      <img
        src={src}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />

      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        style={{
          position: "absolute",
          top: 1,
          right: 1,
          background:
            "rgba(0,0,0,0.75)",
          border: "none",
          borderRadius: 3,
          color: "#f87171",
          fontSize: 10,
          lineHeight: 1,
          padding: "1px 3px",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        ×
      </button>

      <div
        style={{
          position: "absolute",
          bottom: 1,
          left: 2,
          fontSize: 9,
          color:
            "rgba(255,255,255,0.5)",
          userSelect: "none",
        }}
      >
        {index + 1}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PROGRESS BAR
// ---------------------------------------------------------------------------

function ProgressBar({
  value,
  total,
  label,
}) {
  const pct =
    total > 0
      ? Math.round(
          (value / total) * 100
        )
      : 0;

  return (
    <div
      style={{
        marginTop: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "#64748b",
          }}
        >
          {label}
        </span>

        <span
          style={{
            fontSize: 11,
            color: "#38bdf8",
          }}
        >
          {value} / {total} ({pct}%)
        </span>
      </div>

      <div
        style={{
          background: "#1e293b",
          borderRadius: 4,
          height: 6,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background:
              "linear-gradient(90deg,#0ea5e9,#38bdf8)",
            transition:
              "width 0.2s",
            borderRadius: 4,
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

export default function VCardGenerator() {
  // -------------------------------------------------------------------------
  // PAGE / GRID STATE
  // -------------------------------------------------------------------------

  const [selectedPage, setSelectedPage] =
    useState(
      "305x460_portrait"
    );

  const [selectedGrid, setSelectedGrid] =
    useState("24_portrait");

  // NEW:
  // Page margin and card-to-card gap are completely independent.
  const [outerMarginMM, setOuterMarginMM] =
    useState(5);

  const [gridGapMM, setGridGapMM] =
    useState(5);

  // IMAGE SIZE ADJUSTMENT — independent 0.1 mm controls.
  const [imageWidthAdjustMM, setImageWidthAdjustMM] =
    useState(0);
  const [imageHeightAdjustMM, setImageHeightAdjustMM] =
    useState(0);

  // -------------------------------------------------------------------------
  // MODE
  // -------------------------------------------------------------------------

  const [mode, setMode] =
    useState("single");

  const [repeatMode, setRepeatMode] =
    useState("one");

  // -------------------------------------------------------------------------
  // IMAGES
  // -------------------------------------------------------------------------

  const [images, setImages] =
    useState([]);

  const [previewIdx, setPreviewIdx] =
    useState(0);

  // -------------------------------------------------------------------------
  // UI
  // -------------------------------------------------------------------------

  const [generating, setGenerating] =
    useState(false);

  const [progress, setProgress] =
    useState({
      done: 0,
      total: 0,
    });

  const [isDragging, setIsDragging] =
    useState(false);

  const [zipLoading, setZipLoading] =
    useState(false);

  // -------------------------------------------------------------------------
  // REFS
  // -------------------------------------------------------------------------

  const canvasRef =
    useRef(null);

  const fileInputRef =
    useRef(null);

  const zipInputRef =
    useRef(null);

  // -------------------------------------------------------------------------
  // DERIVED PAGE / GRID
  // -------------------------------------------------------------------------

  const {
    width: pageW,
    height: pageH,
  } =
    pageOptions[selectedPage];

  const gridDef =
    gridOptions.find(
      (g) =>
        g.id === selectedGrid
    );

  const {
    cols,
    rows,
    imgW: cellImgW,
    imgH: cellImgH,
  } =
    gridDef.computeGrid(
      pageW,
      pageH,
      outerMarginMM,
      gridGapMM
    );

  const totalCards =
    rows * cols;

  const previewImg =
    images[
      previewIdx
    ]?.dataUrl ?? null;

  const isBigGrid =
    BIG_GRID_IDS.has(
      selectedGrid
    );

  const rotateVertical =
    selectedGrid === "1x2" &&
    pageW > pageH;

  // -------------------------------------------------------------------------
  // LAYOUT DESCRIPTION
  // -------------------------------------------------------------------------

  const layoutIsMaximum =
    outerMarginMM === 0 &&
    gridGapMM === 0;

  const layoutDescription =
    layoutIsMaximum
      ? "Maximum space — no page margin and no space between cards."
      : outerMarginMM === 0 &&
        gridGapMM > 0
      ? "No page margin — only the selected space remains between cards."
      : outerMarginMM > 0 &&
        gridGapMM === 0
      ? "Page margin enabled — cards touch each other with no space between them."
      : "Page margin and card-to-card space are both enabled.";

  // -------------------------------------------------------------------------
  // PRESET HELPERS
  // -------------------------------------------------------------------------

  const applyPreset = (
    preset
  ) => {
    switch (preset) {
      case "margin-space":
        setOuterMarginMM(5);
        setGridGapMM(5);
        break;

      case "margin-no-space":
        setOuterMarginMM(5);
        setGridGapMM(0);
        break;

      case "no-margin-space":
        setOuterMarginMM(0);
        setGridGapMM(5);
        break;

      case "no-margin-no-space":
        setOuterMarginMM(0);
        setGridGapMM(0);
        break;

      default:
        break;
    }
  };

  // -------------------------------------------------------------------------
  // PREVIEW CANVAS
  // -------------------------------------------------------------------------

  useEffect(() => {
    const canvas =
      canvasRef.current;

    if (!canvas) return;

    const ctx =
      canvas.getContext("2d");

    const PPM = 3;

    const cW =
      Math.round(pageW * PPM);

    const cH =
      Math.round(pageH * PPM);

    canvas.width = cW;
    canvas.height = cH;

    ctx.clearRect(
      0,
      0,
      cW,
      cH
    );

    ctx.fillStyle = "#ffffff";

    ctx.fillRect(
      0,
      0,
      cW,
      cH
    );

    const drawGrid = (
      imgData
    ) => {
      const isArray =
        Array.isArray(imgData);

      // ---------------------------------------------------------------
      // SINGLE IMAGE / ONE IMAGE
      // ---------------------------------------------------------------

      if (
        mode === "single" &&
        repeatMode === "one"
      ) {
        const imgObj =
          isArray
            ? imgData[0]
            : imgData;

        const {
          drawable,
          w: natW,
          h: natH,
        } = imgObj
          ? getRenderable(
              imgObj,
              rotateVertical
            )
          : {
              drawable: null,
              w: 0,
              h: 0,
            };

        if (imgObj) {
          const {
            w: baseW,
            h: baseH,
          } =
            containFit(
              natW,
              natH,
              pageW,
              pageH
            );

          const {
            w: dw,
            h: dh,
          } =
            adjustImageSize(
              baseW,
              baseH,
              imageWidthAdjustMM * PPM,
              imageHeightAdjustMM * PPM
            );

          const dx =
            (cW -
              dw * PPM) /
            2;

          const dy =
            (cH -
              dh * PPM) /
            2;

          ctx.drawImage(
            drawable,
            dx,
            dy,
            dw * PPM,
            dh * PPM
          );
        } else {
          drawPlaceholderCanvas(
            ctx,
            0,
            0,
            cW,
            cH,
            PPM
          );
        }

        drawScissorCutMarksCanvas(
          ctx,
          pageW,
          pageH,
          PPM
        );

        return;
      }

      // ---------------------------------------------------------------
      // GRID
      // ---------------------------------------------------------------

      for (
        let r = 0;
        r < rows;
        r++
      ) {
        for (
          let c = 0;
          c < cols;
          c++
        ) {
          const {
            x,
            y,
          } =
            getGridPosition({
              row: r,
              col: c,
              cols,
              rows,
              pageW,
              pageH,
              outerMargin:
                outerMarginMM,
              gridGap:
                gridGapMM,
              cellW:
                cellImgW,
              cellH:
                cellImgH,
            });

          const bx =
            x * PPM;

          const by =
            y * PPM;

          const bw =
            cellImgW * PPM;

          const bh =
            cellImgH * PPM;

          // Cell outline only.
          //
          // When gridGap = 0, neighboring cells touch exactly.
          ctx.strokeStyle =
            "#e2e8f0";

          ctx.lineWidth = 1;

          ctx.strokeRect(
            bx,
            by,
            bw,
            bh
          );

          const idx =
            r * cols + c;

          const imgObj =
            isArray
              ? imgData[idx]
              : imgData;

          if (imgObj) {
            drawContainImageCanvas(
              ctx,
              imgObj,
              bx,
              by,
              bw,
              bh,
              rotateVertical,
              imageWidthAdjustMM * PPM,
              imageHeightAdjustMM * PPM,
              gridGapMM === 0
            );
          } else {
            drawPlaceholderCanvas(
              ctx,
              bx,
              by,
              bw,
              bh,
              PPM
            );
          }
        }
      }

      // ---------------------------------------------------------------
      // CUT MARKS
      // ---------------------------------------------------------------

      drawScissorCutMarksCanvas(
        ctx,
        pageW,
        pageH,
        PPM
      );
    };

    // -----------------------------------------------------------------------
    // LOAD MULTIPLE IMAGES
    // -----------------------------------------------------------------------

    if (
      mode !== "single" &&
      repeatMode === "one" &&
      images.length > 0
    ) {
      Promise.all(
        images
          .slice(
            0,
            totalCards
          )
          .map(
            (item) =>
              new Promise(
                (resolve) => {
                  const img =
                    new window.Image();

                  img.onload = () =>
                    resolve(img);

                  img.src =
                    item.dataUrl;
                }
              )
          )
      ).then(
        (
          loadedImages
        ) => {
          drawGrid(
            loadedImages
          );
        }
      );
    } else if (
      previewImg
    ) {
      const img =
        new window.Image();

      img.onload = () =>
        drawGrid(img);

      img.src =
        previewImg;
    } else {
      drawGrid(null);
    }
  }, [
    previewImg,
    cols,
    rows,
    cellImgW,
    cellImgH,
    outerMarginMM,
    gridGapMM,
    pageW,
    pageH,
    mode,
    repeatMode,
    images,
    rotateVertical,
    totalCards,
    imageWidthAdjustMM,
    imageHeightAdjustMM,
  ]);

  // -------------------------------------------------------------------------
  // IMAGE LOADING
  // -------------------------------------------------------------------------

  const addImages =
    useCallback(
      (newEntries) => {
        setImages(
          (prev) =>
            [
              ...prev,
              ...newEntries,
            ].slice(0, 1000)
        );
      },
      []
    );

  const handleFiles =
    useCallback(
      async (files) => {
        const imgFiles =
          Array.from(files)
            .filter((f) =>
              f.type.startsWith(
                "image/"
              )
            )
            .slice(0, 1000);

        const entries =
          await Promise.all(
            imgFiles.map(
              async (f) => ({
                dataUrl:
                  await readFileAsDataURL(
                    f
                  ),
                name:
                  f.name,
              })
            )
          );

        addImages(
          entries
        );

        setPreviewIdx(
          (prev) => prev
        );
      },
      [addImages]
    );

  // -------------------------------------------------------------------------
  // ZIP
  // -------------------------------------------------------------------------

  const handleZip =
    useCallback(
      async (file) => {
        if (!file) return;

        setZipLoading(true);

        try {
          if (!window.JSZip) {
            await new Promise(
              (
                resolve,
                reject
              ) => {
                const script =
                  document.createElement(
                    "script"
                  );

                script.src =
                  "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";

                script.onload =
                  resolve;

                script.onerror =
                  reject;

                document.head.appendChild(
                  script
                );
              }
            );
          }

          const JSZip =
            window.JSZip;

          const zip =
            await JSZip.loadAsync(
              file
            );

          const imgExts =
            /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

          const entries = [];
          const tasks = [];

          zip.forEach(
            (
              path,
              entry
            ) => {
              if (
                !entry.dir &&
                imgExts.test(
                  path
                )
              ) {
                tasks.push(
                  entry
                    .async(
                      "base64"
                    )
                    .then(
                      (b64) => {
                        const ext =
                          path
                            .split(
                              "."
                            )
                            .pop()
                            .toLowerCase();

                        const mime =
                          ext ===
                            "jpg" ||
                          ext ===
                            "jpeg"
                            ? "image/jpeg"
                            : ext ===
                              "png"
                            ? "image/png"
                            : ext ===
                              "gif"
                            ? "image/gif"
                            : ext ===
                              "webp"
                            ? "image/webp"
                            : "image/png";

                        entries.push({
                          dataUrl:
                            `data:${mime};base64,${b64}`,
                          name:
                            path,
                        });
                      }
                    )
                );
              }
            }
          );

          await Promise.all(
            tasks
          );

          entries.sort(
            (a, b) =>
              a.name.localeCompare(
                b.name
              )
          );

          addImages(
            entries.slice(
              0,
              1000
            )
          );
        } catch (err) {
          alert(
            "Failed to load ZIP: " +
              err.message
          );
        } finally {
          setZipLoading(false);
        }
      },
      [addImages]
    );

  // -------------------------------------------------------------------------
  // DRAG & DROP
  // -------------------------------------------------------------------------

  const handleDrop =
    useCallback(
      (e) => {
        e.preventDefault();

        setIsDragging(false);

        const files =
          Array.from(
            e.dataTransfer.files
          );

        if (
          files.length === 1 &&
          files[0].name
            .toLowerCase()
            .endsWith(".zip")
        ) {
          handleZip(
            files[0]
          );
        } else {
          handleFiles(
            files
          );
        }
      },
      [
        handleFiles,
        handleZip,
      ]
    );

  // -------------------------------------------------------------------------
  // PDF GENERATION
  // -------------------------------------------------------------------------

  const handleGenerate =
    async () => {
      if (
        images.length === 0
      ) {
        return;
      }

      setGenerating(true);

      await new Promise(
        (res) =>
          setTimeout(
            res,
            50
          )
      );

      try {
        const orientation =
          pageW > pageH
            ? "l"
            : "p";

        // ---------------------------------------------------------------
        // SINGLE MODE
        // ---------------------------------------------------------------

        if (
          mode === "single"
        ) {
          const pdf =
            new jsPDF(
              orientation,
              "mm",
              [
                pageW,
                pageH,
              ]
            );

          const imgObj =
            await loadImageObj(
              images[0]
                .dataUrl
            );

          const {
            drawable,
            w: natW,
            h: natH,
          } =
            getRenderable(
              imgObj,
              rotateVertical
            );

          // -------------------------------------------------------------
          // ONE IMAGE ON PAGE
          // -------------------------------------------------------------

          if (
            repeatMode ===
            "one"
          ) {
            const {
              w: baseW,
              h: baseH,
            } =
              containFit(
                natW,
                natH,
                pageW,
                pageH
              );

            const {
              w: dw,
              h: dh,
            } =
              adjustImageSize(
                baseW,
                baseH,
                imageWidthAdjustMM,
                imageHeightAdjustMM
              );

            const bx =
              (pageW - dw) /
              2;

            const by =
              (pageH - dh) /
              2;

            pdf.addImage(
              drawable,
              "PNG",
              bx,
              by,
              dw,
              dh
            );

            setProgress({
              done: 1,
              total: 1,
            });
          }

          // -------------------------------------------------------------
          // SAME IMAGE REPEATED
          // -------------------------------------------------------------

          else {
            setProgress({
              done: 0,
              total:
                totalCards,
            });

            for (
              let r = 0;
              r < rows;
              r++
            ) {
              for (
                let c = 0;
                c < cols;
                c++
              ) {
                const {
                  w: dw,
                  h: dh,
                } =
                  getGridImageSize(
                    natW,
                    natH,
                    cellImgW,
                    cellImgH,
                    imageWidthAdjustMM,
                    imageHeightAdjustMM,
                    gridGapMM === 0
                  );

                const {
                  x: cellX,
                  y: cellY,
                } =
                  getGridPosition({
                    row: r,
                    col: c,
                    cols,
                    rows,
                    pageW,
                    pageH,
                    outerMargin:
                      outerMarginMM,
                    gridGap:
                      gridGapMM,
                    cellW:
                      cellImgW,
                    cellH:
                      cellImgH,
                  });

                const bx =
                  cellX +
                  (cellImgW -
                    dw) /
                    2;

                const by =
                  cellY +
                  (cellImgH -
                    dh) /
                    2;

                pdf.addImage(
                  drawable,
                  "PNG",
                  bx,
                  by,
                  dw,
                  dh
                );

                setProgress(
                  (p) => ({
                    ...p,
                    done:
                      p.done + 1,
                  })
                );

                await new Promise(
                  (res) =>
                    setTimeout(
                      res,
                      0
                    )
                );
              }
            }
          }

          drawScissorCutMarks(
            pdf,
            pageW,
            pageH
          );

          pdf.save(
            `vcard_${selectedGrid}_margin-${outerMarginMM}mm_gap-${gridGapMM}mm_w-${imageWidthAdjustMM.toFixed(1)}mm_h-${imageHeightAdjustMM.toFixed(1)}mm.pdf`
          );

          return;
        }

        // ---------------------------------------------------------------
        // BULK / ZIP — UNIQUE IMAGE MODE
        // ---------------------------------------------------------------

        const pdf =
          new jsPDF(
            orientation,
            "mm",
            [
              pageW,
              pageH,
            ]
          );

        if (
          repeatMode ===
          "one"
        ) {
          const imagesPerPage =
            totalCards;

          const totalPages =
            Math.ceil(
              images.length /
                imagesPerPage
            );

          setProgress({
            done: 0,
            total:
              totalPages,
          });

          for (
            let pageIndex = 0;
            pageIndex <
            totalPages;
            pageIndex++
          ) {
            if (
              pageIndex > 0
            ) {
              pdf.addPage(
                [
                  pageW,
                  pageH,
                ],
                orientation
              );
            }

            const startImageIndex =
              pageIndex *
              imagesPerPage;

            for (
              let cellIndex = 0;
              cellIndex <
              imagesPerPage;
              cellIndex++
            ) {
              const imageIndex =
                startImageIndex +
                cellIndex;

              if (
                imageIndex >=
                images.length
              ) {
                break;
              }

              const imgObj =
                await loadImageObj(
                  images[
                    imageIndex
                  ].dataUrl
                );

              const {
                drawable,
                w: natW,
                h: natH,
              } =
                getRenderable(
                  imgObj,
                  rotateVertical
                );

              const {
                w: dw,
                h: dh,
              } =
                getGridImageSize(
                  natW,
                  natH,
                  cellImgW,
                  cellImgH,
                  imageWidthAdjustMM,
                  imageHeightAdjustMM,
                  gridGapMM === 0
                );

              const row =
                Math.floor(
                  cellIndex /
                    cols
                );

              const col =
                cellIndex %
                cols;

              const {
                x: cellX,
                y: cellY,
              } =
                getGridPosition({
                  row,
                  col,
                  cols,
                  rows,
                  pageW,
                  pageH,
                  outerMargin:
                    outerMarginMM,
                  gridGap:
                    gridGapMM,
                  cellW:
                    cellImgW,
                  cellH:
                    cellImgH,
                });

              const bx =
                cellX +
                (cellImgW -
                  dw) /
                  2;

              const by =
                cellY +
                (cellImgH -
                  dh) /
                  2;

              pdf.addImage(
                drawable,
                "PNG",
                bx,
                by,
                dw,
                dh
              );
            }

            drawScissorCutMarks(
              pdf,
              pageW,
              pageH
            );

            setProgress({
              done:
                pageIndex + 1,
              total:
                totalPages,
            });

            await new Promise(
              (res) =>
                setTimeout(
                  res,
                  0
                )
            );
          }

          pdf.save(
            `vcards_unique_images_margin-${outerMarginMM}mm_gap-${gridGapMM}mm_w-${imageWidthAdjustMM.toFixed(1)}mm_h-${imageHeightAdjustMM.toFixed(1)}mm.pdf`
          );
        }

        // ---------------------------------------------------------------
        // SAME IMAGE MODE
        // ---------------------------------------------------------------

        else {
          const total =
            images.length;

          setProgress({
            done: 0,
            total,
          });

          for (
            let i = 0;
            i < total;
            i++
          ) {
            if (i > 0) {
              pdf.addPage(
                [
                  pageW,
                  pageH,
                ],
                orientation
              );
            }

            const imgObj =
              await loadImageObj(
                images[i]
                  .dataUrl
              );

            const {
              drawable,
              w: natW,
              h: natH,
            } =
              getRenderable(
                imgObj,
                rotateVertical
              );

            for (
              let r = 0;
              r < rows;
              r++
            ) {
              for (
                let c = 0;
                c < cols;
                c++
              ) {
                const {
                  w: dw,
                  h: dh,
                } =
                  getGridImageSize(
                    natW,
                    natH,
                    cellImgW,
                    cellImgH,
                    imageWidthAdjustMM,
                    imageHeightAdjustMM,
                    gridGapMM === 0
                  );

                const {
                  x: cellX,
                  y: cellY,
                } =
                  getGridPosition({
                    row: r,
                    col: c,
                    cols,
                    rows,
                    pageW,
                    pageH,
                    outerMargin:
                      outerMarginMM,
                    gridGap:
                      gridGapMM,
                    cellW:
                      cellImgW,
                    cellH:
                      cellImgH,
                  });

                const bx =
                  cellX +
                  (cellImgW -
                    dw) /
                    2;

                const by =
                  cellY +
                  (cellImgH -
                    dh) /
                    2;

                pdf.addImage(
                  drawable,
                  "PNG",
                  bx,
                  by,
                  dw,
                  dh
                );
              }
            }

            drawScissorCutMarks(
              pdf,
              pageW,
              pageH
            );

            setProgress({
              done: i + 1,
              total,
            });

            await new Promise(
              (res) =>
                setTimeout(
                  res,
                  0
                )
            );
          }

          pdf.save(
            `vcards_bulk_${total}_images_margin-${outerMarginMM}mm_gap-${gridGapMM}mm_w-${imageWidthAdjustMM.toFixed(1)}mm_h-${imageHeightAdjustMM.toFixed(1)}mm.pdf`
          );
        }
      } catch (error) {
        console.error(
          error
        );

        alert(
          "PDF generation failed: " +
            error.message
        );
      } finally {
        setGenerating(false);

        setProgress({
          done: 0,
          total: 0,
        });
      }
    };

  // -------------------------------------------------------------------------
  // DERIVED UI
  // -------------------------------------------------------------------------

  const mmToCm =
    (mm) =>
      (mm / 10).toFixed(
        1
      );

  const canGen =
    images.length > 0 &&
    !generating;

  // -------------------------------------------------------------------------
  // UPLOAD ZONE
  // -------------------------------------------------------------------------

  const uploadZone = (
    <div
      onClick={() =>
        mode === "zip"
          ? zipInputRef.current?.click()
          : fileInputRef.current?.click()
      }
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() =>
        setIsDragging(false)
      }
      onDrop={handleDrop}
      style={{
        border:
          `1.5px dashed ${
            isDragging
              ? "#38bdf8"
              : images.length
              ? "#22c55e"
              : "#334155"
          }`,
        borderRadius: 8,
        padding: "1.25rem",
        textAlign: "center",
        cursor: "pointer",
        transition:
          "all 0.2s",
        background:
          isDragging
            ? "#0c1a2e"
            : "transparent",
      }}
    >
      {zipLoading ? (
        <p
          style={{
            fontSize: 13,
            color: "#38bdf8",
            margin: 0,
          }}
        >
          Extracting ZIP…
        </p>
      ) : images.length > 0 ? (
        <>
          <p
            style={{
              fontSize: 12,
              color: "#22c55e",
              margin:
                "0 0 4px",
            }}
          >
            {images.length} image
            {images.length > 1
              ? "s"
              : ""}{" "}
            loaded
          </p>

          <p
            style={{
              fontSize: 11,
              color: "#475569",
              margin: 0,
            }}
          >
            {mode === "zip"
              ? "Drop another ZIP or click to replace"
              : "Drop more images or click to add"}
          </p>
        </>
      ) : (
        <>
          <p
            style={{
              fontSize: 22,
              margin:
                "0 0 8px",
              color: "#334155",
            }}
          >
            ↑
          </p>

          <p
            style={{
              fontSize: 13,
              color: "#475569",
              margin: 0,
            }}
          >
            {mode === "zip"
              ? "Drop a ZIP file or click to browse"
              : mode === "bulk"
              ? "Drop up to 1000 images or click to browse"
              : "Drop an image or click to browse"}
          </p>
        </>
      )}
    </div>
  );

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------

  return (
    <div
      style={{
        minHeight:
          "100vh",
        background:
          "#0a0a0f",
        fontFamily:
          "'DM Mono','Fira Mono',monospace",
        color:
          "#e2e8f0",
        display:
          "flex",
        flexDirection:
          "column",
      }}
    >
      {/* HEADER */}

      <header
        style={{
          borderBottom:
            "1px solid #1e293b",
          padding:
            "1.25rem 2rem",
          display:
            "flex",
          alignItems:
            "baseline",
          gap: "1rem",
        }}
      >
        <span
          style={{
            fontSize: 11,
            letterSpacing:
              "0.15em",
            color:
              "#64748b",
            textTransform:
              "uppercase",
          }}
        >
          VCARDGEN
        </span>

        <h1
          style={{
            fontSize: 20,
            fontWeight: 400,
            margin: 0,
            color:
              "#f8fafc",
            letterSpacing:
              "-0.02em",
          }}
        >
          Visiting Card PDF Generator
        </h1>
      </header>

      {/* MAIN AREA */}

      <div
        style={{
          flex: 1,
          display:
            "grid",
          gridTemplateColumns:
            "340px 1fr",
          overflow:
            "hidden",
        }}
      >
        {/* SIDEBAR */}

        <aside
          style={{
            borderRight:
              "1px solid #1e293b",
            padding:
              "1.5rem",
            display:
              "flex",
            flexDirection:
              "column",
            gap:
              "1.25rem",
            overflowY:
              "auto",
          }}
        >
          {/* PAPER SIZE */}

          <section>
            <Label>
              Paper Size
            </Label>

            <Select
              value={
                selectedPage
              }
              onChange={(e) =>
                setSelectedPage(
                  e.target.value
                )
              }
            >
              {Object.entries(
                pageOptions
              ).map(
                ([
                  key,
                  value,
                ]) => (
                  <option
                    key={key}
                    value={key}
                  >
                    {value.label}
                  </option>
                )
              )}
            </Select>

            <Hint>
              {pageW} ×{" "}
              {pageH} mm (
              {mmToCm(
                pageW
              )} ×{" "}
              {mmToCm(
                pageH
              )} cm)
            </Hint>
          </section>

          {/* GRID */}

          <section>
            <Label>
              Grid Layout
            </Label>

            <div
              style={{
                display:
                  "flex",
                flexDirection:
                  "column",
                gap: 5,
              }}
            >
              {gridOptions.map(
                (g) => (
                  <button
                    key={g.id}
                    onClick={() =>
                      setSelectedGrid(
                        g.id
                      )
                    }
                    style={{
                      background:
                        selectedGrid ===
                        g.id
                          ? "#1e293b"
                          : "transparent",
                      border:
                        `1px solid ${
                          selectedGrid ===
                          g.id
                            ? "#334155"
                            : "#1e293b"
                        }`,
                      borderRadius: 6,
                      color:
                        selectedGrid ===
                        g.id
                          ? "#f1f5f9"
                          : "#64748b",
                      padding:
                        "9px 12px",
                      fontSize: 13,
                      cursor:
                        "pointer",
                      textAlign:
                        "left",
                      fontFamily:
                        "inherit",
                      transition:
                        "all 0.15s",
                      display:
                        "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "space-between",
                    }}
                  >
                    <span>
                      {g.label}
                    </span>

                    {selectedGrid ===
                      g.id && (
                      <span
                        style={{
                          fontSize: 10,
                          color:
                            "#38bdf8",
                        }}
                      >
                        ●
                      </span>
                    )}
                  </button>
                )
              )}
            </div>

            {isBigGrid && (
              <Hint>
                Images are scaled up as much
                as possible while preserving
                their original aspect ratio.
              </Hint>
            )}
          </section>

          {/* ---------------------------------------------------------------
              LAYOUT SPACING
          --------------------------------------------------------------- */}

          <section>
            <Label>
              Layout Spacing
            </Label>

            {/* QUICK PRESETS */}

            <div
              style={{
                display:
                  "grid",
                gridTemplateColumns:
                  "1fr 1fr",
                gap: 6,
                marginBottom: 12,
              }}
            >
              <PresetButton
                active={
                  outerMarginMM ===
                    5 &&
                  gridGapMM === 5
                }
                onClick={() =>
                  applyPreset(
                    "margin-space"
                  )
                }
              >
                Margin + Space
              </PresetButton>

              <PresetButton
                active={
                  outerMarginMM ===
                    5 &&
                  gridGapMM === 0
                }
                onClick={() =>
                  applyPreset(
                    "margin-no-space"
                  )
                }
              >
                Margin + No Space
              </PresetButton>

              <PresetButton
                active={
                  outerMarginMM ===
                    0 &&
                  gridGapMM === 5
                }
                onClick={() =>
                  applyPreset(
                    "no-margin-space"
                  )
                }
              >
                No Margin + Space
              </PresetButton>

              <PresetButton
                active={
                  outerMarginMM ===
                    0 &&
                  gridGapMM === 0
                }
                onClick={() =>
                  applyPreset(
                    "no-margin-no-space"
                  )
                }
              >
                No Margin + No Space
              </PresetButton>
            </div>

            {/* PAGE MARGIN */}

            <div
              style={{
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  marginBottom: 5,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color:
                      "#94a3b8",
                  }}
                >
                  Page Margin
                </span>

                <span
                  style={{
                    fontSize: 11,
                    color:
                      outerMarginMM ===
                      0
                        ? "#22c55e"
                        : "#38bdf8",
                  }}
                >
                  {outerMarginMM} mm
                </span>
              </div>

              <input
                type="number"
                step="0.1"
                min="0"
                max="20"
                value={
                  outerMarginMM
                }
                onChange={(e) =>
                  setOuterMarginMM(
                    Math.max(
                      0,
                      Math.min(
                        20,
                        parseFloat(
                          e.target
                            .value
                        ) || 0
                      )
                    )
                  )
                }
                style={{
                  width:
                    "100%",
                  background:
                    "#111827",
                  border:
                    "1px solid #1e293b",
                  borderRadius: 6,
                  color:
                    "#e2e8f0",
                  padding:
                    "9px 12px",
                  fontSize: 13,
                  fontFamily:
                    "inherit",
                  boxSizing:
                    "border-box",
                }}
              />

              <p
                style={{
                  fontSize: 10,
                  color:
                    "#475569",
                  margin:
                    "4px 0 0",
                  lineHeight:
                    1.4,
                }}
              >
                Distance from the
                paper edge to the
                first/last card.
              </p>
            </div>

            {/* GRID GAP */}

            <div>
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  marginBottom: 5,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color:
                      "#94a3b8",
                  }}
                >
                  Space Between Cards
                </span>

                <span
                  style={{
                    fontSize: 11,
                    color:
                      gridGapMM === 0
                        ? "#22c55e"
                        : "#38bdf8",
                  }}
                >
                  {gridGapMM} mm
                </span>
              </div>

              <input
                type="number"
                step="0.1"
                min="0"
                max="20"
                value={
                  gridGapMM
                }
                onChange={(e) =>
                  setGridGapMM(
                    Math.max(
                      0,
                      Math.min(
                        20,
                        parseFloat(
                          e.target
                            .value
                        ) || 0
                      )
                    )
                  )
                }
                style={{
                  width:
                    "100%",
                  background:
                    "#111827",
                  border:
                    "1px solid #1e293b",
                  borderRadius: 6,
                  color:
                    "#e2e8f0",
                  padding:
                    "9px 12px",
                  fontSize: 13,
                  fontFamily:
                    "inherit",
                  boxSizing:
                    "border-box",
                }}
              />

              <p
                style={{
                  fontSize: 10,
                  color:
                    "#475569",
                  margin:
                    "4px 0 0",
                  lineHeight:
                    1.4,
                }}
              >
                Distance between
                neighboring cards.
                Enter 0 for absolutely
                no space.
              </p>
            </div>

            {/* STATUS */}

            <div
              style={{
                marginTop: 10,
                background:
                  "#0f172a",
                border:
                  "1px solid #1e293b",
                borderRadius: 6,
                padding:
                  "8px 10px",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color:
                    layoutIsMaximum
                      ? "#22c55e"
                      : "#38bdf8",
                  lineHeight:
                    1.5,
                }}
              >
                {layoutDescription}
              </div>
            </div>
          </section>

          {/* ---------------------------------------------------------------
              IMAGE SIZE ADJUSTMENT
          --------------------------------------------------------------- */}

          <section>
            <Label>Image Size Adjustment</Label>

            <SizeAdjustControl
              label="Horizontal / Width"
              value={imageWidthAdjustMM}
              onChange={setImageWidthAdjustMM}
            />

            <SizeAdjustControl
              label="Vertical / Height"
              value={imageHeightAdjustMM}
              onChange={setImageHeightAdjustMM}
            />

            <button
              type="button"
              onClick={() => {
                setImageWidthAdjustMM(0);
                setImageHeightAdjustMM(0);
              }}
              style={{ width: "100%", background: "transparent", border: "1px solid #1e293b", borderRadius: 6, color: "#64748b", padding: "7px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
            >
              Reset Image Size
            </button>

            <Hint>
              Width and height are adjusted independently in exact 0.1 mm steps.
              The same adjustment is applied to the PDF.
            </Hint>
          </section>

          {/* ---------------------------------------------------------------
              SUMMARY
          --------------------------------------------------------------- */}

          <div
            style={{
              background:
                "#0f172a",
              border:
                "1px solid #1e293b",
              borderRadius: 8,
              padding:
                "0.9rem",
            }}
          >
            <p
              style={{
                fontSize: 11,
                color:
                  "#64748b",
                margin:
                  "0 0 8px",
                textTransform:
                  "uppercase",
                letterSpacing:
                  "0.1em",
              }}
            >
              Layout Summary
            </p>

            <div
              style={{
                display:
                  "grid",
                gridTemplateColumns:
                  "1fr 1fr",
                gap: 8,
              }}
            >
              {[
                [
                  "Columns",
                  cols,
                ],
                [
                  "Rows",
                  rows,
                ],
                [
                  "Cards / page",
                  totalCards,
                ],
                [
                  isBigGrid
                    ? "Max cell"
                    : "Cell",
                  `${cellImgW.toFixed(
                    1
                  )} × ${cellImgH.toFixed(
                    1
                  )} mm`,
                ],
                [
                  "Page margin",
                  `${outerMarginMM} mm`,
                ],
                [
                  "Card spacing",
                  `${gridGapMM} mm`,
                ],
              ].map(
                ([
                  label,
                  value,
                ]) => (
                  <div
                    key={label}
                  >
                    <p
                      style={{
                        fontSize: 10,
                        color:
                          "#475569",
                        margin:
                          "0 0 2px",
                      }}
                    >
                      {label}
                    </p>

                    <p
                      style={{
                        fontSize: 13,
                        color:
                          "#f1f5f9",
                        margin: 0,
                        fontWeight:
                          500,
                      }}
                    >
                      {value}
                    </p>
                  </div>
                )
              )}
            </div>

            {/* MAXIMUM MODE */}

            {layoutIsMaximum && (
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 8,
                  borderTop:
                    "1px solid #1e293b",
                }}
              >
                <p
                  style={{
                    fontSize: 10,
                    color:
                      "#22c55e",
                    margin: 0,
                    lineHeight:
                      1.5,
                  }}
                >
                  ✓ Maximum paper utilization
                  <br />
                  ✓ 0 mm page margin
                  <br />
                  ✓ 0 mm between cards
                  <br />
                  ✓ Grid touches page edges
                </p>
              </div>
            )}
          </div>

          {/* ---------------------------------------------------------------
              UPLOAD MODE
          --------------------------------------------------------------- */}

          <section>
            <Label>
              Upload Mode
            </Label>

            <div
              style={{
                display:
                  "flex",
                gap: 6,
                marginBottom:
                  10,
              }}
            >
              {MODES.map(
                (m) => (
                  <button
                    key={m}
                    onClick={() =>
                      setMode(m)
                    }
                    style={{
                      flex: 1,
                      background:
                        mode ===
                        m
                          ? "#1e293b"
                          : "transparent",
                      border:
                        `1px solid ${
                          mode ===
                          m
                            ? "#38bdf8"
                            : "#1e293b"
                        }`,
                      borderRadius: 6,
                      color:
                        mode ===
                        m
                          ? "#38bdf8"
                          : "#64748b",
                      padding:
                        "7px 4px",
                      fontSize: 11,
                      cursor:
                        "pointer",
                      fontFamily:
                        "inherit",
                      transition:
                        "all 0.15s",
                      textAlign:
                        "center",
                    }}
                  >
                    {MODE_LABELS[m]}
                  </button>
                )
              )}
            </div>

            <p
              style={{
                fontSize: 11,
                color:
                  "#475569",
                margin:
                  "0 0 8px",
                lineHeight:
                  1.5,
              }}
            >
              {mode ===
                "single" && (
                <span
                  style={{
                    display:
                      "flex",
                    flexDirection:
                      "column",
                    gap: 6,
                  }}
                >
                  {[
                    {
                      val: "one",
                      label:
                        "One image",
                      desc:
                        "Image placed once, centered on the page.",
                    },
                    {
                      val: "same",
                      label:
                        "Same image",
                      desc:
                        "Image repeated across all grid cells.",
                    },
                  ].map(
                    ({
                      val,
                      label,
                      desc,
                    }) => (
                      <label
                        key={
                          val
                        }
                        style={{
                          display:
                            "flex",
                          alignItems:
                            "flex-start",
                          gap: 8,
                          cursor:
                            "pointer",
                        }}
                      >
                        <input
                          type="radio"
                          name="repeatMode"
                          value={
                            val
                          }
                          checked={
                            repeatMode ===
                            val
                          }
                          onChange={() =>
                            setRepeatMode(
                              val
                            )
                          }
                          style={{
                            marginTop: 2,
                            accentColor:
                              "#38bdf8",
                          }}
                        />

                        <span>
                          <span
                            style={{
                              fontSize: 12,
                              color:
                                "#e2e8f0",
                              display:
                                "block",
                            }}
                          >
                            {
                              label
                            }
                          </span>

                          <span
                            style={{
                              fontSize: 11,
                              color:
                                "#475569",
                            }}
                          >
                            {
                              desc
                            }
                          </span>
                        </span>
                      </label>
                    )
                  )}
                </span>
              )}

              {mode ===
                "bulk" &&
                "Multiple images → each image gets its own PDF page, tiled."}

              {mode ===
                "zip" &&
                "Upload a ZIP containing images → each image becomes a PDF page."}
            </p>

            {/* UPLOAD */}

            {uploadZone}

            {/* FILE INPUT */}

            <input
              ref={
                fileInputRef
              }
              type="file"
              accept="image/*"
              multiple={
                mode !==
                "single"
              }
              onChange={(e) =>
                handleFiles(
                  e.target
                    .files
                )
              }
              style={{
                display:
                  "none",
              }}
            />

            {/* ZIP INPUT */}

            <input
              ref={
                zipInputRef
              }
              type="file"
              accept=".zip,application/zip"
              onChange={(e) =>
                handleZip(
                  e.target
                    .files[0]
                )
              }
              style={{
                display:
                  "none",
              }}
            />

            {/* THUMBNAILS */}

            {images.length >
              0 && (
              <div
                style={{
                  marginTop: 10,
                }}
              >
                <div
                  style={{
                    display:
                      "flex",
                    gap: 6,
                    flexWrap:
                      "wrap",
                    maxHeight:
                      108,
                    overflowY:
                      "auto",
                    padding:
                      "4px 2px",
                  }}
                >
                  {images.map(
                    (
                      img,
                      i
                    ) => (
                      <Thumbnail
                        key={
                          i
                        }
                        src={
                          img.dataUrl
                        }
                        index={
                          i
                        }
                        selected={
                          i ===
                          previewIdx
                        }
                        onSelect={(
                          idx
                        ) =>
                          setPreviewIdx(
                            idx
                          )
                        }
                        onRemove={(
                          idx
                        ) => {
                          setImages(
                            (
                              prev
                            ) =>
                              prev.filter(
                                (
                                  _,
                                  j
                                ) =>
                                  j !==
                                  idx
                              )
                          );

                          setPreviewIdx(
                            (
                              p
                            ) =>
                              Math.max(
                                0,
                                p >=
                                  idx
                                  ? p -
                                      1
                                  : p
                              )
                          );
                        }}
                      />
                    )
                  )}
                </div>

                <div
                  style={{
                    display:
                      "flex",
                    justifyContent:
                      "space-between",
                    marginTop: 6,
                  }}
                >
                  <p
                    style={{
                      fontSize: 11,
                      color:
                        "#475569",
                      margin: 0,
                    }}
                  >
                    {
                      images.length
                    }
                    /1000 image
                    {images.length !==
                    1
                      ? "s"
                      : ""}
                    {mode !==
                      "single" &&
                      ` → ${images.length} PDF page${
                        images.length !==
                        1
                          ? "s"
                          : ""
                      }`}
                  </p>

                  <button
                    onClick={() => {
                      setImages(
                        []
                      );
                      setPreviewIdx(
                        0
                      );
                    }}
                    style={{
                      background:
                        "transparent",
                      border:
                        "none",
                      color:
                        "#f87171",
                      fontSize: 11,
                      cursor:
                        "pointer",
                      fontFamily:
                        "inherit",
                      padding: 0,
                    }}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}

            {/* PROGRESS */}

            {generating &&
              progress.total >
                0 && (
                <ProgressBar
                  value={
                    progress.done
                  }
                  total={
                    progress.total
                  }
                  label={
                    mode ===
                    "single"
                      ? "Placing cards…"
                      : "Processing images…"
                  }
                />
              )}
          </section>

          {/* GENERATE */}

          <button
            onClick={
              handleGenerate
            }
            disabled={
              !canGen
            }
            style={{
              background:
                canGen
                  ? "#38bdf8"
                  : "#1e293b",
              border:
                "none",
              borderRadius: 8,
              color:
                canGen
                  ? "#0a0a0f"
                  : "#334155",
              padding:
                "13px",
              fontSize: 14,
              fontWeight: 600,
              cursor:
                canGen
                  ? "pointer"
                  : "not-allowed",
              fontFamily:
                "inherit",
              letterSpacing:
                "0.05em",
              transition:
                "all 0.2s",
            }}
          >
            {generating
              ? `Generating… (${progress.done}/${progress.total})`
              : images.length ===
                0
              ? "Upload an image first"
              : mode ===
                "single"
              ? "Generate PDF"
              : `Generate PDF (${images.length} page${
                  images.length !==
                  1
                    ? "s"
                    : ""
                })`}
          </button>
        </aside>

        {/* ---------------------------------------------------------------
            PREVIEW
        --------------------------------------------------------------- */}

        <main
          style={{
            display:
              "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            padding:
              "2rem",
            background:
              "#050508",
            overflow:
              "auto",
          }}
        >
          <div
            style={{
              textAlign:
                "center",
            }}
          >
            <p
              style={{
                fontSize: 11,
                color:
                  "#334155",
                marginBottom:
                  "1rem",
                letterSpacing:
                  "0.1em",
                textTransform:
                  "uppercase",
              }}
            >
              Live preview —{" "}
              {pageW} ×{" "}
              {pageH} mm

              {images.length >
                1 && (
                <span
                  style={{
                    color:
                      "#38bdf8",
                    marginLeft: 8,
                  }}
                >
                  (image{" "}
                  {previewIdx +
                    1}{" "}
                  of{" "}
                  {
                    images.length
                  }
                  )
                </span>
              )}
            </p>

            {/* NAVIGATION */}

            {images.length >
              1 && (
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "center",
                  gap: 8,
                  marginBottom:
                    12,
                }}
              >
                <NavBtn
                  onClick={() =>
                    setPreviewIdx(
                      (
                        p
                      ) =>
                        Math.max(
                          0,
                          p -
                            1
                        )
                    )
                  }
                  disabled={
                    previewIdx ===
                    0
                  }
                >
                  ◀ Prev
                </NavBtn>

                <span
                  style={{
                    fontSize: 12,
                    color:
                      "#475569",
                    lineHeight:
                      "30px",
                  }}
                >
                  {previewIdx +
                    1}{" "}
                  /{" "}
                  {
                    images.length
                  }
                </span>

                <NavBtn
                  onClick={() =>
                    setPreviewIdx(
                      (
                        p
                      ) =>
                        Math.min(
                          images.length -
                            1,
                          p +
                            1
                        )
                    )
                  }
                  disabled={
                    previewIdx ===
                    images.length -
                      1
                  }
                >
                  Next ▶
                </NavBtn>
              </div>
            )}

            {/* PAGE */}

            <div
              style={{
                boxShadow:
                  "0 0 0 1px #1e293b, 0 20px 60px rgba(0,0,0,0.8)",
                display:
                  "inline-block",
                borderRadius: 4,
                overflow:
                  "hidden",
              }}
            >
              <canvas
                ref={
                  canvasRef
                }
                style={{
                  display:
                    "block",
                  maxWidth:
                    "min(600px, 100%)",
                  maxHeight:
                    "65vh",
                  objectFit:
                    "contain",
                  width:
                    "auto",
                  height:
                    "auto",
                }}
              />
            </div>

            {images.length ===
              0 && (
              <p
                style={{
                  fontSize: 12,
                  color:
                    "#334155",
                  marginTop:
                    "1rem",
                }}
              >
                Upload an image to
                preview cards
              </p>
            )}

            {/* CURRENT SPACING INFO */}

            <div
              style={{
                marginTop: 12,
                fontSize: 10,
                color:
                  "#475569",
                lineHeight:
                  1.6,
              }}
            >
              Page margin:{" "}
              <strong
                style={{
                  color:
                    "#94a3b8",
                }}
              >
                {
                  outerMarginMM
                }{" "}
                mm
              </strong>
              {" • "}
              Space between cards:{" "}
              <strong
                style={{
                  color:
                    "#94a3b8",
                }}
              >
                {
                  gridGapMM
                }{" "}
                mm
              </strong>

              {" • "}
              Image adjustment: {imageWidthAdjustMM >= 0 ? "+" : ""}
              {imageWidthAdjustMM.toFixed(1)} × {imageHeightAdjustMM >= 0 ? "+" : ""}
              {imageHeightAdjustMM.toFixed(1)} mm

              {layoutIsMaximum && (
                <>
                  {" • "}
                  <strong
                    style={{
                      color:
                        "#22c55e",
                    }}
                  >
                    MAXIMUM SPACE
                  </strong>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SMALL UI HELPERS
// ---------------------------------------------------------------------------

const sizeAdjustButtonStyle = {
  background: "#111827",
  border: "1px solid #1e293b",
  borderRadius: 6,
  color: "#94a3b8",
  padding: "6px",
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "inherit",
};

function SizeAdjustControl({ label, value, onChange }) {
  const update = (next) => {
    onChange(Math.round(next * 10) / 10);
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>{label}</span>
        <span style={{ fontSize: 11, color: value === 0 ? "#64748b" : "#38bdf8" }}>
          {value > 0 ? "+" : ""}{value.toFixed(1)} mm
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 36px", gap: 5 }}>
        <button type="button" onClick={() => update(value - 0.1)} style={sizeAdjustButtonStyle}>−</button>
        <input
          type="number"
          step="0.1"
          value={value}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            update(Number.isFinite(parsed) ? parsed : 0);
          }}
          style={{ width: "100%", boxSizing: "border-box", background: "#111827", border: "1px solid #1e293b", borderRadius: 6, color: "#e2e8f0", padding: "8px", fontSize: 12, fontFamily: "inherit", textAlign: "center" }}
        />
        <button type="button" onClick={() => update(value + 0.1)} style={sizeAdjustButtonStyle}>+</button>
      </div>
    </div>
  );
}

function Label({
  children,
}) {
  return (
    <label
      style={{
        display:
          "block",
        fontSize: 11,
        letterSpacing:
          "0.1em",
        color:
          "#64748b",
        marginBottom:
          7,
        textTransform:
          "uppercase",
      }}
    >
      {children}
    </label>
  );
}

function Hint({
  children,
}) {
  return (
    <p
      style={{
        fontSize: 11,
        color:
          "#475569",
        marginTop: 5,
        lineHeight:
          1.5,
      }}
    >
      {children}
    </p>
  );
}

function Select({
  value,
  onChange,
  children,
}) {
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        width:
          "100%",
        background:
          "#111827",
        border:
          "1px solid #1e293b",
        borderRadius: 6,
        color:
          "#e2e8f0",
        padding:
          "9px 12px",
        fontSize: 13,
        cursor:
          "pointer",
        fontFamily:
          "'DM Mono','Fira Mono',monospace",
      }}
    >
      {children}
    </select>
  );
}

function PresetButton({
  active,
  onClick,
  children,
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background:
          active
            ? "#1e293b"
            : "#0b1220",
        border:
          `1px solid ${
            active
              ? "#38bdf8"
              : "#1e293b"
          }`,
        borderRadius: 6,
        color:
          active
            ? "#38bdf8"
            : "#64748b",
        padding:
          "7px 5px",
        fontSize: 10,
        cursor:
          "pointer",
        fontFamily:
          "inherit",
        lineHeight:
          1.3,
        transition:
          "all 0.15s",
      }}
    >
      {children}
    </button>
  );
}

function NavBtn({
  onClick,
  disabled,
  children,
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background:
          disabled
            ? "transparent"
            : "#1e293b",
        border:
          `1px solid ${
            disabled
              ? "#0f172a"
              : "#334155"
          }`,
        borderRadius: 5,
        color:
          disabled
            ? "#1e293b"
            : "#94a3b8",
        padding:
          "4px 10px",
        fontSize: 11,
        cursor:
          disabled
            ? "default"
            : "pointer",
        fontFamily:
          "'DM Mono','Fira Mono',monospace",
        transition:
          "all 0.15s",
      }}
    >
      {children}
    </button>
  );
}